/**
 * 설명서·소개서 내려받기.
 *
 * 콘솔 안에서 읽는 것이 기본이지만, **콘솔을 못 여는 자리**가 있다 — 교육장, 인수인계
 * 자리, 현장 점검, 도입을 검토하는 회의. 그래서 두 가지를 파일로 내보낸다.
 *
 *   설명서   스무 장 전부. 쓰는 법을 찾아 읽는 사람의 것.
 *   소개서   콘솔을 한 번도 열어 본 적 없는 사람에게 "무엇을 하는 물건인가" 를 말한다.
 *
 * **파일은 이미 서버에 있다.** 예전에는 누르면 브라우저가 그 자리에서 문서를 지었다 —
 * 조판은 1초면 끝나지만 그 1초를 위해 생성기와 캡처 2.2MB 를 먼저 받아야 했고, 현장
 * 회선에서 그 대기가 "만드는 중" 으로 길게 보였다. 지금은 빌드 때 만들어 두고
 * (`npm run docs`) 여기서는 그 파일을 가리키기만 한다. 누르면 브라우저가 받는다.
 *
 * 판은 둘을 둔다 — 현재 판과 **이전 판**. 새 판을 낸 뒤 "전에 보내 주신 것" 을 다시
 * 찾는 일이 있고, 새 판에 문제가 있으면 그 자리에서 되돌릴 수 있어야 한다. 이전 판은
 * 앞에 내세우지 않고 작게 둔다.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FileDown, FileText, MonitorPlay, Presentation } from 'lucide-react';
import { DOWNLOAD_DIR, DOWNLOADS, type DownloadEntry, type DownloadSet } from '../manual/downloads.js';

/**
 * 받는 사람에게 보일 이름 — 이름에 날짜를 박는다.
 *
 * 여러 판이 한 폴더에 쌓였을 때 어느 것이 최신인지 보인다. 서버에 놓인 파일 이름은
 * 아스키로 고정해 두고(주소가 인코딩을 거치며 어긋나지 않게), 보이는 이름은 여기서 붙인다.
 */
export function downloadName(entry: DownloadEntry, builtAt: string): string {
  return `SignageOne-${entry.name}-${builtAt.replaceAll('-', '')}.${entry.extension}`;
}

const mb = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)}MB`;

/**
 * 파일 주소 — **해시가 붙지 않는 고정 주소**다.
 *
 * 번들러에게 맡기면 판마다 이름이 바뀌는데, 배포 전에 열어 둔 탭은 옛 이름을 들고 있고 지난
 * 판 파일도 서버에 남아 있어 **옛 문서가 조용히 내려왔다**(2026-09-23 운영). 이름을 고정하면
 * 오래 열어 둔 탭도 늘 최신 파일을 받는다.
 *
 * `BASE_URL` 은 독립 빌드에서 `/`, iframe 임베드 빌드에서 `./` 다 — 임베드에서는 콘솔이
 * 놓인 자리를 기준으로 풀린다(주소는 해시 라우팅이라 화면을 옮겨도 그 자리가 바뀌지 않는다).
 */
export function downloadHref(entry: DownloadEntry): string {
  return `${import.meta.env.BASE_URL}${DOWNLOAD_DIR}/${entry.file}`;
}

export function ManualDownload(): ReactNode {
  const { current, previous } = DOWNLOADS;

  const link = (set: DownloadSet, id: string, text: string): ReactNode => {
    const entry = set.files[id];
    if (!entry) return null;
    return (
      <a
        href={downloadHref(entry)}
        download={downloadName(entry, set.builtAt)}
        className="inline-flex h-7 items-center gap-1.5 rounded-md bg-white/10 px-2.5 text-[12.5px] font-medium text-white ring-1 ring-white/15 transition-colors hover:bg-white/20"
      >
        {id.endsWith('pdf') ? (
          <FileDown className="h-4 w-4" strokeWidth={2} />
        ) : (
          <Presentation className="h-4 w-4" strokeWidth={2} />
        )}
        {text}
        <span className="text-white/50">{mb(entry.bytes)}</span>
      </a>
    );
  };

  /** 이전 판 — 글자와 색을 낮춰 현재 판과 헷갈리지 않게 한다. */
  const older = (pdf: string, pptx: string): ReactNode => {
    if (!previous) return null;
    const one = (id: string, text: string): ReactNode => {
      const entry = previous.files[id];
      if (!entry) return null;
      return (
        <a
          href={downloadHref(entry)}
          download={downloadName(entry, previous.builtAt)}
          className="underline decoration-slate-600 underline-offset-2 transition-colors hover:text-slate-200"
        >
          {text}
        </a>
      );
    };
    return (
      <span className="inline-flex items-center gap-2 text-[11.5px] text-slate-500">
        <span>이전 판 {previous.builtAt}</span>
        {one(pdf, 'PDF')}
        {one(pptx, 'PPT')}
      </span>
    );
  };

  /** 한 줄 = 문서 하나. 왼쪽에 무엇인지, 오른쪽에 어떤 형식으로 받을지. `view` 가 있으면 콘솔에서 바로 본다. */
  const row = (label: string, note: string, pdf: string, pptx: string, view?: string): ReactNode => (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex min-w-[9.5rem] items-center gap-1.5 text-[12.5px] text-slate-300">
        <FileText className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        {label}
        <span className="text-slate-400">{note}</span>
      </span>
      {view && (
        <Link
          to={view}
          className="inline-flex h-7 items-center gap-1.5 rounded-md bg-th-5 px-2.5 text-[12.5px] font-semibold text-th-3 transition-colors hover:bg-th-6"
        >
          <MonitorPlay className="h-4 w-4" strokeWidth={2} />
          화면으로 보기
        </Link>
      )}
      {link(current, pdf, 'PDF')}
      {link(current, pptx, 'PPT')}
      {older(pdf, pptx)}
    </div>
  );

  return (
    <div className="mt-5 flex flex-col gap-2">
      {row('사용 설명서', '전체 장·화면 그림', 'manual-pdf', 'manual-pptx')}
      {row('시스템 소개서', '도입 검토용', 'overview-pdf', 'overview-pptx', '/overview')}
      <p className="text-[12px] text-slate-400">{current.builtAt} 판</p>
    </div>
  );
}
